'use client';
import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

type Category = 'All' | 'Whitepapers' | 'Industry Guides' | 'Staffing Templates' | 'Checklists';

interface Resource {
  id: string;
  category: Exclude<Category, 'All'>;
  title: string;
  description: string;
  pages: string;
  fileType: string;
  fileSize: string;
  tags: string[];
  featured?: boolean;
}

const resources: Resource[] = [
  {
    id: 'it-talent-shortage-2025',
    category: 'Whitepapers',
    title: 'Navigating the 2025 IT Talent Shortage: A Strategic Playbook',
    description:
      'An in-depth analysis of the global IT talent gap, sector-by-sector demand forecasts, and proven strategies for securing top-tier technology professionals before your competitors do.',
    pages: '28 pages',
    fileType: 'PDF',
    fileSize: '2.4 MB',
    tags: ['Talent Strategy', 'Workforce Planning', 'IT Hiring'],
    featured: true,
  },
  {
    id: 'diversity-tech-hiring',
    category: 'Whitepapers',
    title: 'Building Diverse Technology Teams: Evidence-Based Hiring Practices',
    description:
      'Research-backed frameworks for reducing bias in technical interviews, sourcing from underrepresented talent pools, and measuring diversity outcomes across your IT workforce.',
    pages: '22 pages',
    fileType: 'PDF',
    fileSize: '1.8 MB',
    tags: ['DEI', 'Inclusive Hiring', 'Tech Teams'],
    featured: true,
  },
  {
    id: 'rpo-vs-traditional',
    category: 'Whitepapers',
    title: 'RPO vs. Traditional Staffing: Total Cost of Ownership Analysis',
    description:
      'A quantitative comparison of recruitment process outsourcing against conventional staffing models — covering cost-per-hire, time-to-fill, quality-of-hire, and long-term retention metrics.',
    pages: '18 pages',
    fileType: 'PDF',
    fileSize: '1.5 MB',
    tags: ['RPO', 'Cost Analysis', 'Staffing Models'],
  },
  {
    id: 'fintech-hiring-guide',
    category: 'Industry Guides',
    title: 'FinTech IT Hiring Guide: Skills, Compliance & Compensation',
    description:
      'Everything hiring managers in financial technology need to know — from must-have certifications (SOX, PCI-DSS) to current compensation benchmarks for cloud, security, and data engineering roles.',
    pages: '34 pages',
    fileType: 'PDF',
    fileSize: '3.1 MB',
    tags: ['FinTech', 'Compliance', 'Compensation'],
    featured: true,
  },
  {
    id: 'healthcare-it-guide',
    category: 'Industry Guides',
    title: 'Healthcare IT Staffing Guide: HIPAA, EHR & Emerging Roles',
    description:
      'A practical guide to staffing healthcare technology teams — covering HIPAA-compliant hiring workflows, EHR implementation specialists, and the fastest-growing roles in digital health.',
    pages: '30 pages',
    fileType: 'PDF',
    fileSize: '2.7 MB',
    tags: ['Healthcare', 'HIPAA', 'EHR'],
  },
  {
    id: 'cloud-talent-guide',
    category: 'Industry Guides',
    title: 'Cloud & DevOps Talent Market Guide 2025',
    description:
      'Current market intelligence on AWS, Azure, and GCP talent availability, salary ranges by certification level, and the emerging DevSecOps roles reshaping infrastructure teams.',
    pages: '26 pages',
    fileType: 'PDF',
    fileSize: '2.2 MB',
    tags: ['Cloud', 'DevOps', 'Market Data'],
  },
  {
    id: 'cybersecurity-talent',
    category: 'Industry Guides',
    title: 'Cybersecurity Talent Acquisition in a Zero-Trust World',
    description:
      'How to identify, assess, and retain cybersecurity professionals as threat landscapes evolve — including role definitions, skills matrices, and compensation data for CISO to SOC analyst.',
    pages: '24 pages',
    fileType: 'PDF',
    fileSize: '2.0 MB',
    tags: ['Cybersecurity', 'Zero-Trust', 'CISO'],
  },
  {
    id: 'job-description-template',
    category: 'Staffing Templates',
    title: 'IT Job Description Template Library (50+ Roles)',
    description:
      'Ready-to-use, ATS-optimized job description templates for the most in-demand IT roles — from Software Engineers and Data Scientists to Cloud Architects and Security Analysts.',
    pages: '62 pages',
    fileType: 'DOCX',
    fileSize: '890 KB',
    tags: ['Job Descriptions', 'ATS', 'Templates'],
    featured: true,
  },
  {
    id: 'interview-scorecard',
    category: 'Staffing Templates',
    title: 'Technical Interview Scorecard & Evaluation Framework',
    description:
      'Standardized scorecards for evaluating software engineers, data professionals, and infrastructure specialists — with weighted criteria, rating scales, and debrief facilitation guides.',
    pages: '14 pages',
    fileType: 'XLSX',
    fileSize: '420 KB',
    tags: ['Interviews', 'Evaluation', 'Hiring Process'],
  },
  {
    id: 'contractor-onboarding',
    category: 'Staffing Templates',
    title: 'IT Contractor Onboarding Checklist & SOW Template',
    description:
      'A complete onboarding package for contract IT professionals — including Statement of Work template, access provisioning checklist, compliance sign-offs, and 30/60/90-day milestone tracker.',
    pages: '18 pages',
    fileType: 'PDF',
    fileSize: '650 KB',
    tags: ['Onboarding', 'Contractors', 'SOW'],
  },
  {
    id: 'workforce-planning-template',
    category: 'Staffing Templates',
    title: 'Annual IT Workforce Planning Spreadsheet',
    description:
      'A structured Excel model for forecasting headcount needs, budget allocation, skills gap analysis, and hiring timeline planning across your technology organization.',
    pages: '8 tabs',
    fileType: 'XLSX',
    fileSize: '380 KB',
    tags: ['Workforce Planning', 'Budgeting', 'Forecasting'],
  },
  {
    id: 'vendor-evaluation-checklist',
    category: 'Checklists',
    title: 'IT Staffing Vendor Evaluation Checklist',
    description:
      '25-point checklist for evaluating and comparing IT staffing partners — covering compliance, talent quality, SLA benchmarks, diversity commitments, and contract terms.',
    pages: '6 pages',
    fileType: 'PDF',
    fileSize: '310 KB',
    tags: ['Vendor Selection', 'Due Diligence', 'SLA'],
    featured: true,
  },
  {
    id: 'hiring-compliance-checklist',
    category: 'Checklists',
    title: 'IT Hiring Compliance Checklist: Federal & State Requirements',
    description:
      'Stay audit-ready with this comprehensive checklist covering I-9 verification, background check regulations, pay transparency laws, and contractor classification rules by state.',
    pages: '10 pages',
    fileType: 'PDF',
    fileSize: '480 KB',
    tags: ['Compliance', 'Legal', 'HR'],
  },
  {
    id: 'remote-team-checklist',
    category: 'Checklists',
    title: 'Remote IT Team Setup & Security Checklist',
    description:
      'Ensure every remote hire is productive and secure from day one — covering equipment provisioning, VPN setup, access controls, communication tools, and performance tracking.',
    pages: '8 pages',
    fileType: 'PDF',
    fileSize: '360 KB',
    tags: ['Remote Work', 'Security', 'Setup'],
  },
];

const categories: Category[] = ['All', 'Whitepapers', 'Industry Guides', 'Staffing Templates', 'Checklists'];

const categoryIcons: Record<Exclude<Category, 'All'>, string> = {
  Whitepapers: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  'Industry Guides': 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  'Staffing Templates': 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  Checklists: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2m-3 7l2 2 4-4',
};

const fileTypeColors: Record<string, string> = {
  PDF: '#E53E3E',
  DOCX: '#2B6CB0',
  XLSX: '#276749',
};

export default function ResourcesPage() {
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = resources.filter((r) => {
    const matchesCategory = activeCategory === 'All' || r.category === activeCategory;
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      r.title.toLowerCase().includes(q) ||
      r.description.toLowerCase().includes(q) ||
      r.tags.some((t) => t.toLowerCase().includes(q));
    return matchesCategory && matchesSearch;
  });

  const featured = resources.filter((r) => r.featured);

  return (
    <>
      <Navbar />
      <main style={{ paddingTop: '144px' }}>
        {/* Hero */}
        <section
          className="relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #01142D 0%, #00A7FF 100%)', paddingTop: '72px', paddingBottom: '72px' }}
        >
          {/* Decorative grid */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />
          <div className="container-custom relative z-10">
            <div className="max-w-3xl">
              <span className="section-label" style={{ color: 'rgba(255,255,255,0.7)' }}>
                Knowledge Center
              </span>
              <h1
                className="mt-3 font-light text-white leading-tight"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 200 }}
              >
                Resources for{' '}
                <span style={{ fontWeight: 600, color: '#ffffff' }}>Smarter IT Hiring</span>
              </h1>
              <div className="divider-white mt-5 mb-6" />
              <p className="text-white/80 text-lg leading-relaxed max-w-2xl" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Whitepapers, industry guides, and staffing templates built for HR leaders, talent acquisition teams, and technology executives navigating complex hiring decisions.
              </p>
              {/* Search */}
              <div className="mt-8 flex items-center max-w-xl" style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', backdropFilter: 'blur(8px)' }}>
                <svg className="ml-4 w-5 h-5 text-white/60 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search resources, topics, or keywords…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent px-4 py-3.5 text-white placeholder-white/50 focus:outline-none text-sm"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats bar */}
        <div style={{ background: '#00A7FF' }}>
          <div className="container-custom">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/20">
              {[
                { value: '14', label: 'Free Resources' },
                { value: '4', label: 'Categories' },
                { value: '200K+', label: 'IT Professionals' },
                { value: '500+', label: 'Clients Served' },
              ].map((stat) => (
                <div key={stat.label} className="px-6 py-5 text-center">
                  <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {stat.value}
                  </div>
                  <div className="text-xs text-white/75 mt-0.5 uppercase tracking-wider" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Featured Resources */}
        {activeCategory === 'All' && !searchQuery && (
          <section className="section-padding bg-pinnacle-light">
            <div className="container-custom">
              <div className="flex items-center gap-4 mb-10">
                <div>
                  <span className="section-label">Editor&apos;s Picks</span>
                  <h2 className="section-title mt-1">Featured Resources</h2>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featured.map((resource) => (
                  <FeaturedCard key={resource.id} resource={resource} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* All Resources */}
        <section className="section-padding">
          <div className="container-custom">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-3 mb-10">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-all duration-200"
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    background: activeCategory === cat ? '#00A7FF' : 'white',
                    color: activeCategory === cat ? 'white' : '#48494B',
                    border: `1.5px solid ${activeCategory === cat ? '#00A7FF' : '#e8e8e8'}`,
                    letterSpacing: '0.02em',
                  }}
                >
                  {cat !== 'All' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={categoryIcons[cat as Exclude<Category, 'All'>]} />
                    </svg>
                  )}
                  {cat}
                  <span
                    className="ml-1 text-xs px-1.5 py-0.5"
                    style={{
                      background: activeCategory === cat ? 'rgba(255,255,255,0.25)' : '#f0f9ff',
                      color: activeCategory === cat ? 'white' : '#00A7FF',
                      fontWeight: 700,
                    }}
                  >
                    {cat === 'All' ? resources.length : resources.filter((r) => r.category === cat).length}
                  </span>
                </button>
              ))}
            </div>

            {/* Results count */}
            {(searchQuery || activeCategory !== 'All') && (
              <p className="text-sm text-gray-500 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Showing <strong>{filtered.length}</strong> resource{filtered.length !== 1 ? 's' : ''}
                {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
                {searchQuery ? ` matching "${searchQuery}"` : ''}
              </p>
            )}

            {filtered.length === 0 ? (
              <div className="text-center py-20">
                <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-gray-500" style={{ fontFamily: 'Poppins, sans-serif' }}>No resources found. Try a different search or category.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filtered.map((resource) => (
                  <ResourceCard key={resource.id} resource={resource} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section style={{ background: '#01142D' }} className="py-20">
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="section-label" style={{ color: '#00A7FF' }}>
                  Need Custom Research?
                </span>
                <h2 className="section-title-dark mt-3">
                  Get a Tailored Market<br />
                  <span style={{ fontWeight: 600 }}>Intelligence Report</span>
                </h2>
                <div className="divider-white mt-5 mb-6" />
                <p className="text-white/70 text-lg leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Our research team produces custom talent market reports, compensation benchmarks, and skills gap analyses specific to your industry, geography, and technology stack.
                </p>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', padding: '40px' }}>
                <h3 className="text-white font-semibold text-xl mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Request a Custom Report
                </h3>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Your Name"
                    className="input-field"
                    style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.15)', color: 'white' }}
                  />
                  <input
                    type="email"
                    placeholder="Work Email"
                    className="input-field"
                    style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.15)', color: 'white' }}
                  />
                  <input
                    type="text"
                    placeholder="Company & Industry"
                    className="input-field"
                    style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.15)', color: 'white' }}
                  />
                  <textarea
                    rows={3}
                    placeholder="What research do you need?"
                    className="input-field resize-none"
                    style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.15)', color: 'white' }}
                  />
                  <button className="btn-primary w-full">
                    Submit Request
                    <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function FeaturedCard({ resource }: { resource: Resource }) {
  return (
    <div
      className="card-pinnacle flex flex-col h-full relative overflow-hidden"
      style={{ borderTop: '3px solid #00A7FF' }}
    >
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3 mb-4">
          <span
            className="tag text-xs"
            style={{ background: '#f0f9ff', color: '#00A7FF', border: '1px solid rgba(0,167,255,0.2)' }}
          >
            {resource.category}
          </span>
          <span
            className="text-xs font-bold px-2 py-1"
            style={{
              background: fileTypeColors[resource.fileType] || '#718096',
              color: 'white',
              fontFamily: 'Montserrat, sans-serif',
              letterSpacing: '0.05em',
            }}
          >
            {resource.fileType}
          </span>
        </div>
        <h3
          className="font-semibold text-gray-900 leading-snug mb-3 flex-1"
          style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px' }}
        >
          {resource.title}
        </h3>
        <p className="text-sm text-gray-500 leading-relaxed mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
          {resource.description}
        </p>
        <div className="flex flex-wrap gap-1.5 mb-5">
          {resource.tags.map((tag) => (
            <span
              key={tag}
              className="text-xs px-2.5 py-1"
              style={{ background: '#f7f7f7', color: '#48494B', fontFamily: 'Montserrat, sans-serif', border: '1px solid #e8e8e8' }}
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between pt-4" style={{ borderTop: '1px solid #e8e8e8' }}>
          <div className="flex items-center gap-3 text-xs text-gray-400" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <span>{resource.pages}</span>
            <span>·</span>
            <span>{resource.fileSize}</span>
          </div>
          <button
            className="flex items-center gap-2 text-sm font-semibold transition-all duration-200 hover:gap-3"
            style={{ color: '#00A7FF', fontFamily: 'Montserrat, sans-serif' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download
          </button>
        </div>
      </div>
    </div>
  );
}

function ResourceCard({ resource }: { resource: Resource }) {
  return (
    <div className="card-pinnacle flex gap-5 p-6 group">
      {/* Icon column */}
      <div
        className="flex-shrink-0 flex items-center justify-center"
        style={{ width: '52px', height: '52px', background: '#f0f9ff', border: '1px solid rgba(0,167,255,0.15)' }}
      >
        <svg className="w-6 h-6" fill="none" stroke="#00A7FF" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={categoryIcons[resource.category]} />
        </svg>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span
            className="text-xs font-semibold uppercase tracking-wider"
            style={{ color: '#00A7FF', fontFamily: 'Montserrat, sans-serif' }}
          >
            {resource.category}
          </span>
          <span className="text-gray-300">·</span>
          <span
            className="text-xs font-bold px-1.5 py-0.5"
            style={{
              background: fileTypeColors[resource.fileType] || '#718096',
              color: 'white',
              fontFamily: 'Montserrat, sans-serif',
            }}
          >
            {resource.fileType}
          </span>
        </div>
        <h3
          className="font-semibold text-gray-900 leading-snug mb-2"
          style={{ fontFamily: 'Poppins, sans-serif', fontSize: '15px' }}
        >
          {resource.title}
        </h3>
        <p className="text-sm text-gray-500 leading-relaxed mb-3 line-clamp-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
          {resource.description}
        </p>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 text-xs text-gray-400" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <span>{resource.pages}</span>
            <span>·</span>
            <span>{resource.fileSize}</span>
          </div>
          <button
            className="flex items-center gap-1.5 text-sm font-semibold transition-all duration-200 group-hover:gap-2.5"
            style={{ color: '#00A7FF', fontFamily: 'Montserrat, sans-serif' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Free Download
          </button>
        </div>
      </div>
    </div>
  );
}
